/* ============================================
   COMMENTARY HYBRID ENGINE
   ============================================
   Template-first commentary engine.
   Every event tries the template route first.
   If no template matches → automatic fallback to LLM.

   Same interface as CommentaryEngine (V1).

   Reuses:
   - CommentaryQueue (same priority queue)
   - PossessionPhaseTracker (same tracker)
   - zone-resolver.js
   - template-selector.js
   ============================================ */

import { buildCommentaryContext, buildIdleContext } from './commentary-context.js'
import { CommentaryQueue } from './commentary-queue.js'
import { PossessionPhaseTracker } from './possession-phase-tracker.js'
import { getEventPriority } from '../events/event-mapping.js'
import { state } from '../state.js'
import { resolveZone, flankToSide, zoneToLabel } from './zone-resolver.js'
import { TemplateSelector } from './template-selector.js'
import { TTSStreamClient } from './tts-stream-client.js'
import { getChronoData } from '../ui/radar-chrono.js'

const API_BASE = '/api/commentary'

/* ──────────────────────────────────────────
   Template-first strategy
   ──────────────────────────────────────────
   Every event tries the template route first.
   If no template matches → fallback to LLM.
   To force LLM-only for an event, add it to LLM_ONLY.
   ────────────────────────────────────────── */

const LLM_ONLY = new Set([
  'idle',         // idle needs full LLM context generation
])

/* Event types that use event-based templates (not zone-transition) */
const EVENT_BASED = new Set([
  // P1
  'Corner', 'Frappe cadrée', 'Frappe non cadrée', 'Carton jaune',
  'Coup franc', 'Coup franc dangereux', 'Touche', '6 mètres',
  'Hors-jeu', 'Remplacement', "Coup d'envoi", "Coup d'envoi 2ème mi-temps",
  // P2
  'But', 'Carton rouge', 'Penalty', 'Penalty manqué', 'VAR',
  'Mi-temps', 'Fin du match',
])

export class CommentaryHybridEngine {
  constructor(options = {}) {
    this.enabled = false
    this.audioContext = options.audioContext || null
    this.commentaryGain = null

    // Audio element for playback (used by HTTP fallback)
    this.audioEl = null
    this.sourceNode = null

    // Persistent MediaSource for continuous template playback
    this._persistentMS = null
    this._persistentSB = null
    this._persistentMSReady = false
    this._persistentPending = []
    this._persistentAudioEl = null
    this._persistentSourceNode = null

    // TTS WebSocket client for template route
    this.ttsClient = null
    this._tplSpeakCounter = 0
    this._tplCurrentId = null
    this._tplDoneResolve = null

    // Config
    this.config = {
      volume: options.volume ?? 1.0,
      idleTimerMs: options.idleTimerMs ?? 45000,
      systemPrompt: options.systemPrompt || null,
      lang: options.lang || 'en'
    }

    // Possession phase tracker (reused)
    this.phaseTracker = new PossessionPhaseTracker()

    // Template selector (new)
    this.templateSelector = new TemplateSelector({
      lang: this.config.lang,
      onLog: options.onLog || ((msg) => console.log(msg))
    })

    // Zone tracking
    this._lastZone = null
    this._lastFlank = null
    this._lastTeam = null

    // Turnover flag: stores losing player/team for recovery templates
    this._turnover = null

    // Abort controller for current LLM request
    this.currentController = null

    // Log callback
    this.onLog = options.onLog || ((msg) => console.log(msg))

    // Priority queue (shared logic with V1)
    this.queue = new CommentaryQueue({
      idleTimerMs: this.config.idleTimerMs,
      onGenerate: (item) => this._generate(item),
      onCancel: () => this._cancel(),
      onIdle: () => this._onIdle(),
      onLog: (msg) => this.onLog(msg)
    })

    // Track which items are template vs LLM
    this._currentRoute = null
  }

  /* ──────────────────────────────────────────
     Init & Audio Setup
     ────────────────────────────────────────── */

  init(audioContext) {
    this.audioContext = audioContext || this.audioContext

    if (!this.audioContext) {
      this.onLog('[HYBRID] No AudioContext — text-only mode')
      return
    }

    this.commentaryGain = this.audioContext.createGain()
    this.commentaryGain.gain.value = this.config.volume
    this.commentaryGain.connect(this.audioContext.destination)

    this._createAudioElement()
    this._createPersistentMediaSource()

    this.onLog('[HYBRID] Audio graph initialized')
  }

  _createAudioElement() {
    this.audioEl = document.createElement('audio')
    this.audioEl.preload = 'auto'

    if (this.audioContext && this.commentaryGain) {
      this.sourceNode = this.audioContext.createMediaElementSource(this.audioEl)
      this.sourceNode.connect(this.commentaryGain)
    }

    this.audioEl.addEventListener('ended', () => {
      if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
      if (this._timeupdateHandler) {
        this.audioEl.removeEventListener('timeupdate', this._timeupdateHandler)
        this._timeupdateHandler = null
      }
      this.queue.onCommentaryEnd()
      this.onLog('[HYBRID] Playback ended')
    })

    this.audioEl.addEventListener('error', (e) => {
      if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
      this.onLog(`[HYBRID] Audio error: ${e.message || 'unknown'}`)
      this.queue.onCommentaryEnd()
    })
  }

  /* ──────────────────────────────────────────
     Public API (same as CommentaryEngine)
     ────────────────────────────────────────── */

  enable() {
    this.enabled = true
    // Load templates on enable
    this.templateSelector.load(this.config.lang).catch(err => {
      this.onLog(`[HYBRID] Template load error: ${err.message}`)
    })

    // Connect TTS WebSocket client for template streaming
    this._connectTTSClient()

    this.onLog('[HYBRID] Enabled')
  }

  disable() {
    this.enabled = false
    this._cancel()
    this.queue.destroy()
    this.phaseTracker.reset()
    this.templateSelector.reset()
    this._lastZone = null
    this._lastFlank = null
    this._lastTeam = null
    this._turnover = null
    if (this.ttsClient) {
      this.ttsClient.close()
      this.ttsClient = null
    }
    this.onLog('[HYBRID] Disabled')
  }

  setVolume(v) {
    this.config.volume = v
    if (this.commentaryGain) {
      this.commentaryGain.gain.value = v
    }
  }

  setConfig(cfg) {
    const langChanged = cfg.lang && cfg.lang !== this.config.lang
    Object.assign(this.config, cfg)
    if (cfg.volume !== undefined) this.setVolume(cfg.volume)
    if (cfg.lang) {
      this.templateSelector.load(cfg.lang).catch(() => {})
    }
    if (langChanged && this.enabled) {
      this._connectTTSClient()
    }
  }

  /**
   * Called when an event is detected (from ball-controller or postMessage).
   */
  onEvent(eventType, team = 'neutral') {
    if (!this.enabled) return

    // Feed event to phase tracker
    const x = state.currentBallPosition?.x ?? 0.5
    const y = state.currentBallPosition?.y ?? 0.5
    this.phaseTracker.onEvent(eventType, team, x, y)

    const { priority } = getEventPriority(eventType)
    const forceLL = LLM_ONLY.has(eventType)

    this.onLog(`[HYBRID] Event: "${eventType}" (P${priority}, ${team}, forceLL=${forceLL})`)

    // Template-first: lightweight context for all events.
    // LLM_ONLY events skip templates entirely.
    let context
    if (forceLL) {
      context = buildCommentaryContext(eventType, team, this.phaseTracker)
      context._route = 'llm'
    } else {
      context = { _route: 'template-first', eventType, team, x, y }
    }

    this.queue.push(eventType, priority, team, context)
  }

  getStatus() {
    return {
      enabled: this.enabled,
      hasAudio: !!this.audioContext,
      volume: this.config.volume,
      engineType: 'hybrid',
      queue: this.queue.getStatus()
    }
  }

  /* ──────────────────────────────────────────
     Internal: Generate dispatcher
     ────────────────────────────────────────── */

  async _generate(item) {
    const route = item.context?._route || 'llm'

    if (route === 'llm') {
      // Forced LLM (idle, etc.)
      this._currentRoute = 'llm'
      await this._generateFromLLM(item)
    } else {
      // Template-first: try template, fallback to LLM if no match
      this._currentRoute = 'template'
      const handled = await this._tryTemplate(item)
      if (!handled) {
        this.onLog(`[HYBRID] No template for "${item.eventType}" — falling back to LLM`)
        this._currentRoute = 'llm'
        // Build full LLM context now that we need it
        const llmContext = buildCommentaryContext(item.eventType, item.context.team, this.phaseTracker)
        llmContext._route = 'llm'
        item.context = llmContext
        await this._generateFromLLM(item)
      }
    }
  }

  /* ──────────────────────────────────────────
     Template route
     ────────────────────────────────────────── */

  /**
   * Try template route. Returns true if a template was found and played,
   * false if no template matched (caller should fallback to LLM).
   */
  async _tryTemplate(item) {
    const { eventType, team, x: rawX, y: rawY } = item.context

    // ── Turnover: store context and skip commentary ──
    if (eventType === 'Ballons perdus') {
      const playerInfo = state.lastPlayerInfo
      let lostBy = ''
      if (playerInfo?.playerName) {
        lostBy = playerInfo.playerName
        if (state.playerMapping && playerInfo.playerId) {
          const mapped = state.playerMapping[playerInfo.playerId]
          if (mapped) lostBy = mapped.shortName || mapped.name || lostBy
        }
        lostBy = lostBy.replace(/^([A-Za-z\u00C0-\u024F]\.\s*)+/, '').trim() || lostBy
      }
      this._turnover = { lostBy, team }
      this.onLog(`[HYBRID/TPL] Turnover flagged (lostBy="${lostBy}", team=${team}) — skipping`)
      this.queue.onCommentaryEnd()
      return true
    }

    this.onLog(`[HYBRID/TPL] Trying template for "${eventType}"...`)

    // ── Consume turnover flag ──
    const turnover = this._turnover
    if (turnover && this._turnover.team !== team) {
      // Recovery: different team now has the ball
      this._turnover = null
    } else if (turnover) {
      // Same team regained — cancel turnover
      this._turnover = null
    }

    let text

    if (EVENT_BASED.has(eventType)) {
      // Event-based template (P1/P2): query by eventType
      const vars = this._buildTemplateVars(team, null, null)
      if (turnover && turnover.team !== team) vars.lostBy = turnover.lostBy
      text = this.templateSelector.selectForEvent(eventType, vars)
    } else {
      // Zone-transition template (P0 flux): query by fromZone → toZone
      let x = rawX ?? state.currentBallPosition?.x ?? 0.5
      let y = rawY ?? state.currentBallPosition?.y ?? 0.5
      if (team === 'away') {
        x = 1 - x
        y = 1 - y
      }
      const { zone: toZone, flank } = resolveZone(x, y)

      if (this._lastTeam && this._lastTeam !== team) {
        this._lastZone = null
      }
      const fromZone = this._lastZone

      this.onLog(`[HYBRID/TPL] Zone: ${fromZone || '(start)'} -> ${toZone} (flank=${flank || 'none'})${turnover && turnover.team !== team ? ' [RECOVERY]' : ''}`)

      const vars = this._buildTemplateVars(team, flank, toZone)
      const isRecovery = turnover && turnover.team !== team
      if (isRecovery) vars.lostBy = turnover.lostBy
      text = this.templateSelector.select(fromZone, toZone, flank, vars, { recovery: isRecovery })
      this._lastZone = toZone
      this._lastFlank = flank
      this._lastTeam = team
    }

    if (!text) {
      this.onLog(`[HYBRID/TPL] No template found for "${eventType}"`)
      return false
    }

    this.onLog(`[HYBRID/TPL] Selected: "${text}"`)

    // Send to TTS and play
    try {
      await this._ttsAndPlay(text)
      return true
    } catch (err) {
      this.onLog(`[HYBRID/TPL] TTS error: ${err.message}`)
      this.queue.onCommentaryEnd()
      return true  // TTS failed but template was found — don't double-generate via LLM
    }
  }

  /**
   * Build the variables object for template interpolation.
   */
  _buildTemplateVars(team, flank, toZone) {
    const vars = {
      side: flankToSide(flank, this.config.lang),
      zone: zoneToLabel(toZone, this.config.lang),
    }

    // Player names
    const playerInfo = state.lastPlayerInfo
    if (playerInfo?.playerName) {
      let name = playerInfo.playerName
      if (state.playerMapping && playerInfo.playerId) {
        const mapped = state.playerMapping[playerInfo.playerId]
        if (mapped) {
          name = mapped.shortName || mapped.name || name
        }
      }
      // Extract surname
      vars.player = name.replace(/^([A-Za-z\u00C0-\u024F]\.\s*)+/, '').trim() || name
    }

    // Previous player from phase tracker
    const phase = this.phaseTracker.getCurrentPhase()
    if (phase && phase.events.length >= 2) {
      const prevEvent = phase.events[phase.events.length - 2]
      if (prevEvent?.player) {
        const prev = prevEvent.player
        vars.prevPlayer = prev.replace(/^([A-Za-z\u00C0-\u024F]\.\s*)+/, '').trim() || prev
      }
    }

    // ── Deduplication: same player twice in a row ──
    // If prevPlayer === player AND the last two events are consecutive
    // possession types (Possession, Attaque, Attaque dangereuse) with no
    // interrupting event between them, this is a ball carry (conduite de balle).
    if (vars.prevPlayer && vars.player && vars.prevPlayer === vars.player) {
      if (phase && phase.events.length >= 2) {
        const last = phase.events[phase.events.length - 1]
        const prev = phase.events[phase.events.length - 2]
        const POSSESSION_TYPES = ['Possession', 'Attaque', 'Attaque dangereuse']
        if (POSSESSION_TYPES.includes(last?.type) && POSSESSION_TYPES.includes(prev?.type)) {
          vars.ballCarry = true  // signal conduite de balle for template selection
        }
      }
      vars.prevPlayer = null  // prevent "Mbappe pour Mbappe"
    }

    // Team names
    const sd = state.staticDetail
    if (sd) {
      const isHome = team === 'home'
      const possTeam = isHome ? sd.homeTeam : sd.awayTeam
      const oppTeam = isHome ? sd.awayTeam : sd.homeTeam
      vars.team = possTeam?.short_name || possTeam?.name || ''
      vars.opponent = oppTeam?.short_name || oppTeam?.name || ''
      // Goalkeeper (opponent's GK — the one facing the attacking team)
      if (state.playerMapping) {
        const players = Object.values(state.playerMapping)
        const gkSide = isHome ? 'away' : 'home'
        const gk = players.find(p => p.position === 1 && p.team === gkSide)
        if (gk) {
          const gkName = gk.shortName || gk.name || ''
          vars.goalkeeper = gkName.replace(/^([A-Za-z\u00C0-\u024F]\.\s*)+/, '').trim() || gkName
        }
      }
    }

    // Minute & score
    const chrono = getChronoData()
    if (chrono.minute > 0) vars.minute = String(chrono.minute)
    vars.score = state.lastScore || '0-0'

    return vars
  }

  /* ──────────────────────────────────────────
     TTS WebSocket client management
     ────────────────────────────────────────── */

  _connectTTSClient() {
    if (this.ttsClient) {
      this.ttsClient.close()
    }

    this.ttsClient = new TTSStreamClient({
      lang: this.config.lang,
      onAudioChunk: (base64Chunk, id) => {
        this._appendPersistentChunk(base64Chunk)
      },
      onDone: (id) => {
        this.onLog(`[HYBRID/TPL] audio_done for "${id}"`)
        if (this._tplDoneResolve && this._tplCurrentId === id) {
          const resolve = this._tplDoneResolve
          this._tplDoneResolve = null
          this._tplCurrentId = null
          resolve()
        }
      },
      onError: (message) => {
        this.onLog(`[HYBRID/TPL] WS error: ${message}`)
      },
      onLog: (msg) => this.onLog(msg),
    })

    this.ttsClient.connect().catch(err => {
      this.onLog(`[HYBRID/TPL] WS connect error: ${err.message}`)
    })
  }

  /* ──────────────────────────────────────────
     Persistent MediaSource (for template WS route)
     ────────────────────────────────────────── */

  _createPersistentMediaSource() {
    if (typeof MediaSource === 'undefined' || !MediaSource.isTypeSupported('audio/mpeg')) {
      this.onLog('[HYBRID] MediaSource not supported — persistent MS disabled')
      return
    }

    this._persistentAudioEl = document.createElement('audio')
    this._persistentAudioEl.preload = 'auto'

    if (this.audioContext && this.commentaryGain) {
      this._persistentSourceNode = this.audioContext.createMediaElementSource(this._persistentAudioEl)
      this._persistentSourceNode.connect(this.commentaryGain)
    }

    this._initPersistentMS()

    this._persistentAudioEl.addEventListener('error', () => {
      this.onLog('[HYBRID/TPL] Persistent audio error — reinitializing')
      this._initPersistentMS()
    })
  }

  _initPersistentMS() {
    this._persistentMSReady = false
    this._persistentPending = []

    this._persistentMS = new MediaSource()
    this._persistentAudioEl.src = URL.createObjectURL(this._persistentMS)

    this._persistentMS.addEventListener('sourceopen', () => {
      try {
        this._persistentSB = this._persistentMS.addSourceBuffer('audio/mpeg')
        this._persistentMSReady = true

        this._flushPersistentPending()

        this._persistentSB.addEventListener('updateend', () => {
          this._flushPersistentPending()
        })

        this.onLog('[HYBRID/TPL] Persistent MediaSource ready')
      } catch (e) {
        this.onLog(`[HYBRID/TPL] Persistent MS error: ${e.message}`)
      }
    })
  }

  _appendPersistentChunk(base64Chunk) {
    const binary = atob(base64Chunk)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }

    if (this._persistentMSReady && this._persistentSB && !this._persistentSB.updating) {
      try {
        this._persistentSB.appendBuffer(bytes)
        if (this._persistentAudioEl.paused && this._persistentAudioEl.readyState >= 2) {
          this._persistentAudioEl.play().catch(() => {})
        }
      } catch (e) {
        this._persistentPending.push(bytes)
      }
    } else {
      this._persistentPending.push(bytes)
    }

    // Start playback as soon as we have some data
    if (this._persistentAudioEl.paused) {
      this._persistentAudioEl.play().catch(() => {})
    }
  }

  _flushPersistentPending() {
    while (
      this._persistentPending.length > 0 &&
      this._persistentSB &&
      !this._persistentSB.updating
    ) {
      try {
        this._persistentSB.appendBuffer(this._persistentPending.shift())
      } catch (e) {
        break
      }
    }

    if (this._persistentAudioEl && this._persistentAudioEl.paused && this._persistentAudioEl.readyState >= 2) {
      this._persistentAudioEl.play().catch(() => {})
    }
  }

  /* ──────────────────────────────────────────
     TTS via WebSocket — replaces POST /api/tts/preview
     ────────────────────────────────────────── */

  async _ttsAndPlay(text) {
    // Use WebSocket client if available, otherwise fall back to HTTP POST
    if (this.ttsClient && this.ttsClient.isReady) {
      return this._ttsStreamAndPlay(text)
    }
    return this._ttsHttpFallback(text)
  }

  async _ttsStreamAndPlay(text) {
    const id = `tpl_${++this._tplSpeakCounter}`
    this._tplCurrentId = id

    this.onLog(`[HYBRID/TPL] Speaking via WS: "${id}"`)

    // Wait for audio_done from server
    const donePromise = new Promise((resolve) => {
      this._tplDoneResolve = resolve
    })

    const sent = this.ttsClient.speak(id, text)
    if (!sent) {
      this.onLog('[HYBRID/TPL] WS speak failed — falling back to HTTP')
      this._tplDoneResolve = null
      this._tplCurrentId = null
      return this._ttsHttpFallback(text)
    }

    // Safety timeout
    const timeout = setTimeout(() => {
      if (this._tplDoneResolve) {
        this.onLog('[HYBRID/TPL] WS speak timeout')
        const resolve = this._tplDoneResolve
        this._tplDoneResolve = null
        this._tplCurrentId = null
        resolve()
      }
    }, 15000)

    await donePromise
    clearTimeout(timeout)

    this.onLog('[HYBRID/TPL] WS audio done — monitoring playback')

    // Monitor when the persistent audio element has played through
    this._monitorPersistentPlaybackEnd()
  }

  _monitorPersistentPlaybackEnd() {
    if (!this._persistentAudioEl) {
      this.queue.onCommentaryEnd()
      return
    }

    // Safety timeout: longer for LLM route (longer texts) than templates
    const safetyMs = this._currentRoute === 'llm' ? 30000 : 8000
    this._safetyTimer = setTimeout(() => {
      if (this.queue.playing) {
        this.onLog('[HYBRID/TPL] Safety timeout — forcing end')
        this.queue.onCommentaryEnd()
      }
    }, safetyMs)

    // Check if the audio element has caught up with the buffer
    const checkDone = () => {
      if (!this._persistentAudioEl || !this.queue.playing) return
      const buf = this._persistentAudioEl.buffered
      if (buf.length > 0) {
        const buffEnd = buf.end(buf.length - 1)
        const current = this._persistentAudioEl.currentTime
        if (current >= buffEnd - 0.05) {
          if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
          this.queue.onCommentaryEnd()
          this.onLog('[HYBRID/TPL] Persistent playback caught up — done')
          return
        }
      }
      requestAnimationFrame(checkDone)
    }
    // Start checking after a small delay to let the buffer fill
    setTimeout(checkDone, 200)
  }

  /**
   * HTTP fallback for template TTS (original POST /api/tts/preview).
   * Used when WS client is not connected.
   */
  async _ttsHttpFallback(text) {
    this._cancel()
    this.currentController = new AbortController()

    const res = await fetch('/api/tts/preview', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, lang: this.config.lang }),
      signal: this.currentController.signal
    })

    if (!res.ok) {
      throw new Error(`TTS HTTP ${res.status}`)
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    const audioChunks = []
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const block of lines) {
        if (!block.startsWith('data: ')) continue
        try {
          const data = JSON.parse(block.slice(6))
          if (data.type === 'audio' && data.chunk) {
            const binary = atob(data.chunk)
            const bytes = new Uint8Array(binary.length)
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
            audioChunks.push(bytes)
          }
        } catch { /* skip */ }
      }
    }

    if (audioChunks.length > 0) {
      const blob = new Blob(audioChunks, { type: 'audio/mpeg' })
      const url = URL.createObjectURL(blob)

      if (this.audioEl.src && this.audioEl.src.startsWith('blob:')) {
        URL.revokeObjectURL(this.audioEl.src)
      }

      this.audioEl.src = url
      this.audioEl.currentTime = 0

      await this.audioEl.play().catch(() => {})
      this.onLog('[HYBRID/TPL] Playing audio (HTTP fallback)')

      this._safetyTimer = setTimeout(() => {
        if (this.queue.playing) {
          this.onLog('[HYBRID] Safety timeout — forcing end')
          this.queue.onCommentaryEnd()
        }
      }, 8000)
    } else {
      this.onLog(`[HYBRID/TPL] No audio received (text only): "${text}"`)
      this.queue.onCommentaryEnd()
    }
  }

  /* ──────────────────────────────────────────
     LLM route — same pipeline as V1
     ────────────────────────────────────────── */

  async _generateFromLLM(item) {
    const startTime = Date.now()
    this.onLog(`[HYBRID/LLM] Generating for "${item.eventType}"...`)

    this._cancel()
    this.currentController = new AbortController()

    try {
      const body = { ...item.context }
      delete body._route  // remove internal routing field
      if (this.config.systemPrompt) body.systemPrompt = this.config.systemPrompt
      if (this.config.lang) body.lang = this.config.lang

      const response = await fetch(`${API_BASE}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: this.currentController.signal
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      // Process SSE stream (same as V1)
      await this._processSSEStream(response, startTime)
    } catch (err) {
      if (err.name === 'AbortError') {
        this.onLog('[HYBRID/LLM] Request cancelled')
      } else {
        this.onLog(`[HYBRID/LLM] Error: ${err.message}`)
      }
      this.queue.onCommentaryEnd()
    }
  }

  async _processSSEStream(response, startTime) {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    let textAccumulator = ''
    let fullText = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      const text = decoder.decode(value, { stream: true })
      const lines = text.split('\n')

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue

        try {
          const data = JSON.parse(line.slice(6))

          switch (data.type) {
            case 'start':
              this.onLog('[HYBRID/LLM] Pipeline started')
              break

            case 'text':
              textAccumulator += data.chunk
              break

            case 'complete': {
              const totalMs = Date.now() - startTime
              const mode = data.llmMock ? 'MOCK' : 'LIVE'
              fullText = data.text || textAccumulator
              this.onLog(`[HYBRID/LLM] Complete [${mode}]: "${fullText}" (${totalMs}ms)`)
              break
            }

            case 'timeout':
              this.onLog(`[HYBRID/LLM] Pipeline timeout after ${data.elapsed}ms`)
              break

            case 'error':
              this.onLog(`[HYBRID/LLM] Pipeline error: ${data.message}`)
              break
          }
        } catch (e) {
          // Ignore SSE parse errors
        }
      }
    }

    // Use fullText from 'complete' event, or accumulated text as fallback
    const finalText = fullText || textAccumulator

    if (!finalText.trim()) {
      this.onLog('[HYBRID/LLM] No text generated — skipping TTS')
      this.queue.onCommentaryEnd()
      return
    }

    // Send the full LLM text to the WebSocket TTS pipeline (same as templates)
    this.onLog(`[HYBRID/LLM] SSE done, sending to TTS: "${finalText.substring(0, 80)}..."`)
    try {
      await this._ttsAndPlay(finalText)
    } catch (err) {
      this.onLog(`[HYBRID/LLM] TTS error: ${err.message}`)
      this.queue.onCommentaryEnd()
    }
  }

  /* ──────────────────────────────────────────
     Cancel & Idle
     ────────────────────────────────────────── */

  _cancel() {
    if (this._safetyTimer) { clearTimeout(this._safetyTimer); this._safetyTimer = null }
    if (this.currentController) {
      this.currentController.abort()
      this.currentController = null
    }
    if (this.audioEl) {
      this.audioEl.pause()
      this.audioEl.currentTime = 0
    }
    // Cancel pending WS speak
    if (this._tplDoneResolve) {
      const resolve = this._tplDoneResolve
      this._tplDoneResolve = null
      this._tplCurrentId = null
      resolve()
    }
    if (this.ttsClient) {
      this.ttsClient.interrupt()
    }
  }

  _onIdle() {
    if (!this.enabled) return
    this.onLog('[HYBRID] Idle — generating context commentary via LLM')

    const context = buildIdleContext(this.phaseTracker)
    context._route = 'llm'
    this.queue.push('idle', 0, 'neutral', context)
  }

  destroy() {
    this.disable()
    if (this.audioEl) {
      this.audioEl.pause()
      this.audioEl.src = ''
    }
    if (this.sourceNode) {
      try { this.sourceNode.disconnect() } catch (e) {}
    }
    // Clean up persistent MediaSource
    if (this._persistentAudioEl) {
      this._persistentAudioEl.pause()
      try {
        if (this._persistentMS && this._persistentMS.readyState === 'open') {
          this._persistentMS.endOfStream()
        }
      } catch (e) { /* already ended */ }
      this._persistentAudioEl.src = ''
    }
    if (this._persistentSourceNode) {
      try { this._persistentSourceNode.disconnect() } catch (e) {}
    }
    if (this.commentaryGain) {
      try { this.commentaryGain.disconnect() } catch (e) {}
    }
  }
}
